import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import api from '../../../lib/api';
import { useAuthStore } from '../../../store/authStore';
import { formatDisplayDateTime } from '../../../lib/dateUtils';
import SettingsCard from '../../../components/ui/SettingsCard';
import Button from '../../../components/ui/Button';
import Toggle from '../../../components/ui/Toggle';
import Textarea from '../../../components/ui/Textarea';
import PasswordField from '../../../components/ui/PasswordField';

function describeDevice(userAgent) {
  const ua = String(userAgent || '');
  if (!ua) return 'Unknown device';
  let os = 'Unknown OS';
  if (/Windows/i.test(ua)) os = 'Windows';
  else if (/iPhone|iPad|iOS/i.test(ua)) os = 'iOS';
  else if (/Mac OS X|Macintosh/i.test(ua)) os = 'macOS';
  else if (/Android/i.test(ua)) os = 'Android';
  else if (/Linux/i.test(ua)) os = 'Linux';
  let browser = 'browser';
  if (/Edg/i.test(ua)) browser = 'Edge';
  else if (/Chrome/i.test(ua)) browser = 'Chrome';
  else if (/Firefox/i.test(ua)) browser = 'Firefox';
  else if (/Safari/i.test(ua)) browser = 'Safari';
  return `${browser} on ${os}`;
}

function validatePassword(value) {
  const pw = String(value || '');
  if (pw.length < 12) return 'Password must be at least 12 characters';
  if (!/[A-Z]/.test(pw)) return 'Password must include at least one uppercase letter';
  if (!/[a-z]/.test(pw)) return 'Password must include at least one lowercase letter';
  if (!/[0-9]/.test(pw)) return 'Password must include at least one number';
  return null;
}

export default function SecuritySettings() {
  const user = useAuthStore((s) => s.user);
  const logoutLocal = useAuthStore((s) => s.logoutLocal);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const userId = user?.id ?? user?.userId;
  const isTechnician = (user?.role || '').toLowerCase() === 'technician';

  const { data: sessions = [], error: sessionsError } = useQuery(
    ['auth-sessions'],
    () => api.get('/auth/sessions').then((res) => res.data),
    { staleTime: 30 * 1000 }
  );

  const revokeSessionMutation = useMutation(
    (sessionId) => api.delete(`/auth/sessions/${sessionId}`).then((res) => res.data),
    {
      onSuccess: (data) => {
        if (data?.current) {
          logoutLocal();
          queryClient.clear();
          navigate('/login');
          return;
        }
        queryClient.invalidateQueries(['auth-sessions']);
        toast.success('Device signed out');
      },
      onError: (err) => {
        toast.error(err?.response?.data?.error || 'Failed to sign out device');
      },
    }
  );

  const otherSessions = sessions.filter((s) => !s.current);

  const [password, setPassword] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [deletionReason, setDeletionReason] = useState('');

  const passwordMutation = useMutation(
    (data) => api.put(`/users/${userId}/password`, data),
    {
      onSuccess: () => {
        toast.success('Password updated');
        setPassword({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setPasswordErrors({});
      },
      onError: (err) => {
        toast.error(err?.response?.data?.error || 'Failed to update password');
      },
    }
  );

  const deletionRequestMutation = useMutation(
    (payload) => api.post('/users/deletion-requests', payload),
    {
      onSuccess: () => {
        toast.success('Deletion request submitted to admin');
        setDeletionReason('');
      },
      onError: (err) => {
        toast.error(err?.response?.data?.error || 'Failed to submit deletion request');
      },
    }
  );

  const handlePasswordChange = (field) => (e) => {
    const { value } = e.target;
    setPassword((p) => ({ ...p, [field]: value }));
    setPasswordErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (!userId) return;
    const errors = {};
    const newPasswordError = validatePassword(password.newPassword);
    if (!password.currentPassword) errors.currentPassword = 'Current password is required';
    if (newPasswordError) errors.newPassword = newPasswordError;
    if (password.newPassword !== password.confirmPassword) {
      errors.confirmPassword = 'New passwords do not match';
    }
    setPasswordErrors(errors);
    if (Object.keys(errors).length > 0) return;
    passwordMutation.mutate({
      currentPassword: password.currentPassword,
      newPassword: password.newPassword,
    });
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // ignore
    }
    logoutLocal();
    queryClient.clear();
    navigate('/login');
  };

  return (
    <div className="page settings-page">
      <header className="settings-tab-header">
        <h1 className="settings-tab-title">Security</h1>
        <p className="text-muted">Manage your password and account security.</p>
      </header>

      <SettingsCard title="Password">
        <form onSubmit={handlePasswordSubmit}>
          <PasswordField
            id="current-password"
            label="Current password"
            value={password.currentPassword}
            onChange={handlePasswordChange('currentPassword')}
            error={passwordErrors.currentPassword}
            placeholder="••••••••"
            autoComplete="current-password"
          />
          <PasswordField
            id="new-password"
            label="New password"
            value={password.newPassword}
            onChange={handlePasswordChange('newPassword')}
            error={passwordErrors.newPassword}
            placeholder="12+ chars, upper, lower, number"
            minLength={12}
            autoComplete="new-password"
          />
          <PasswordField
            id="confirm-password"
            label="Confirm new password"
            value={password.confirmPassword}
            onChange={handlePasswordChange('confirmPassword')}
            error={passwordErrors.confirmPassword}
            placeholder="••••••••"
            autoComplete="new-password"
          />
          <div className="form-actions">
            <Button type="submit" variant="primary" disabled={passwordMutation.isLoading || !userId}>
              {passwordMutation.isLoading ? 'Updating…' : 'Update password'}
            </Button>
          </div>
        </form>
      </SettingsCard>

      <SettingsCard
        title="Active sessions"
        hint="Devices currently signed in to your account. Sign out any you don't recognise."
      >
        {sessionsError ? (
          <p className="text-muted">Could not load active sessions.</p>
        ) : sessions.length === 0 ? (
          <p className="text-muted">No active sessions found.</p>
        ) : (
          <ul className="session-device-list">
            {sessions.map((s) => (
              <li key={s.id} className="session-device-item">
                <div className="session-device-info">
                  <span className="session-device-name">
                    {describeDevice(s.userAgent)}
                    {s.current && <span className="badge badge-success session-device-badge">This device</span>}
                  </span>
                  <span className="session-device-meta">
                    {s.createdIp || 'Unknown IP'} · Signed in {formatDisplayDateTime(s.createdAt)}
                  </span>
                </div>
                {!s.current && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => revokeSessionMutation.mutate(s.id)}
                    disabled={revokeSessionMutation.isLoading}
                  >
                    Sign out
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
        {otherSessions.length > 0 && (
          <div className="form-actions">
            <Button
              type="button"
              variant="danger"
              disabled={revokeSessionMutation.isLoading}
              onClick={() => otherSessions.forEach((s) => revokeSessionMutation.mutate(s.id))}
            >
              Sign out all other devices
            </Button>
          </div>
        )}
      </SettingsCard>

      <SettingsCard
        title="Two-factor authentication"
        hint="Add a second step at sign-in for extra security. Coming soon."
      >
        <div className="settings-action-row settings-row--between">
          <span className="text-muted">Authenticator app (TOTP)</span>
          <Toggle checked={false} disabled label="Enable 2FA (coming soon)" />
        </div>
      </SettingsCard>

      {isTechnician && (
        <SettingsCard
          title="Account deletion request"
          hint="This creates a non-destructive request for an admin to review."
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              deletionRequestMutation.mutate({ reason: deletionReason.trim() || undefined });
            }}
          >
            <Textarea
              id="deletion-reason"
              label="Reason (optional)"
              rows={3}
              value={deletionReason}
              onChange={(e) => setDeletionReason(e.target.value)}
              placeholder="Provide context for the admin reviewing this request."
            />
            <div className="form-actions">
              <Button type="submit" variant="danger" disabled={deletionRequestMutation.isLoading}>
                {deletionRequestMutation.isLoading ? 'Submitting…' : 'Request account deletion'}
              </Button>
            </div>
          </form>
        </SettingsCard>
      )}

      <SettingsCard title="Account actions">
        <div className="settings-action-row">
          <Link to="/dev-notes" className="btn btn-secondary">
            Dev notes
          </Link>
          <Button type="button" variant="danger" onClick={handleLogout}>
            Log out
          </Button>
        </div>
      </SettingsCard>
    </div>
  );
}
